import {
  AssetModel,
  useGetAssetInteractionsSummaryQuery,
  useGetAssetSelfTestSummaryQuery,
} from '@customer/store/apis/assetsApi';
import styles from './AssetPanelInteractions.module.scss';
import { useTranslation } from 'react-i18next';
import { useLocaleFormatter } from '@customer/shared/hooks/useLocaleFormatter';
import { useAppSelector } from '@customer/store/hooks';
import AssetInfo, { AssetInfoRow } from '@customer/components/asset-data/asset-info/AssetInfo';
import CasAccordion from '@customer/components/accordions/accordion/CasAccordion';
import AccordionTable from '@customer/components/accordions/table/AccordionTable';
import { GridControlled } from '@customer/components/tables/grid/GridControlled';
import { AccordionStatus } from '@customer/components/accordions/types';
import { AssetInteractionsAccordion } from '@customer/store/slices/globalSlice';
import { useMemo } from 'react';
import { ColDef } from 'ag-grid-community';
import {
  columnDefAssetInteractionGI,
  columnDefAssetInteractionGICount,
  columnDefAssetInteractionVI,
  columnDefAssetInteractionVICount,
  lockColumn,
} from '@customer/components/tables/grid/columnDefs';
import AccordionHeaderNumeric from '@customer/components/accordions/header-numeric/AccordionHeaderNumeric';
import NumberRenderer from '@customer/components/tables/cells/number/NumberRenderer';
import { GISummaryModel, VILevel, VISummaryModel } from '@customer/store/apis/interactionsApi';

const gridSliceKeyVI = 'AssetPanelVI';
const gridSliceKeyGI = 'AssetPanelGI';

const getRowIdVI = (data: VISummaryModel) => data.vit;
const getRowIdGI = (data: GISummaryModel) => data.it;

export interface AssetPanelInteractionsProps {
  asset: AssetModel;
}

export function AssetPanelInteractions({ asset }: AssetPanelInteractionsProps) {
  const { t } = useTranslation();
  const { globalTimeRange, showDurationFromSeconds } = useLocaleFormatter();

  const { timeRange, sidePanelDefaultAccordion } = useAppSelector(state => state.global);

  const { currentData: selfTestData } = useGetAssetSelfTestSummaryQuery(
    {
      assetId: asset.id.toString(),
      query: { timeRange },
    },
    { refetchOnMountOrArgChange: 60 },
  );

  const { currentData: interactionsData } = useGetAssetInteractionsSummaryQuery(
    {
      assetId: asset.id.toString(),
      // Use the global time range so that the Self Test summary and Interactions summary are referring to the same time range.
      query: { timeRange },
    },
    { refetchOnMountOrArgChange: 60 },
  );

  const infoRows: AssetInfoRow[] = [
    {
      label: t('SelfTestIgnitionOnTime'),
      value: showDurationFromSeconds(selfTestData?.ign ?? 0),
      emphasis: true,
    },
    {
      label: t('ShowingDataFrom'),
      value: globalTimeRange,
    },
  ];

  const columnDefsVI: ColDef<VISummaryModel>[] = useMemo(
    () => [
      { ...columnDefAssetInteractionVI(t), minWidth: 150 },
      {
        field: 'alarm',
        headerName: t(`vi.level.${VILevel.Alarm}`),
        cellRenderer: NumberRenderer,
      },
      {
        field: 'alert',
        headerName: t(`vi.level.${VILevel.Alert}`),
        cellRenderer: NumberRenderer,
      },
      {
        field: 'aware',
        headerName: t(`vi.level.${VILevel.Aware}`),
        cellRenderer: NumberRenderer,
      },
      { ...columnDefAssetInteractionVICount(t) },
    ],
    [t],
  );

  const columnDefsGI: ColDef<GISummaryModel>[] = useMemo(
    () => [
      { ...columnDefAssetInteractionGI(t), minWidth: 150 },
      { ...columnDefAssetInteractionGICount(t) },
    ],
    [t],
  );

  return (
    <div className={styles.container}>
      <AssetInfo rows={infoRows} />

      <div className={styles.accordions}>
        <CasAccordion
          key={AssetInteractionsAccordion.VehicleInteractions}
          defaultExpanded={
            sidePanelDefaultAccordion === AssetInteractionsAccordion.VehicleInteractions
          }
          status={AccordionStatus.Healthy}
          header={
            <AccordionHeaderNumeric
              title={t('VehicleInteractions')}
              value={interactionsData?.viTot ?? 0}
            />
          }
        >
          <AccordionTable
            hasRows={!!interactionsData?.vi?.length}
            noRowsMessage={t('NoVehicleInteractions')}
          >
            <GridControlled<VISummaryModel>
              autoheight={true}
              sliceKey={gridSliceKeyVI}
              rowData={interactionsData?.vi}
              columnDefs={columnDefsVI}
              defaultColDef={lockColumn}
              getRowId={getRowIdVI}
            />
          </AccordionTable>
        </CasAccordion>

        <CasAccordion
          key={AssetInteractionsAccordion.GeofenceInteractions}
          defaultExpanded={
            sidePanelDefaultAccordion === AssetInteractionsAccordion.GeofenceInteractions
          }
          status={AccordionStatus.Healthy}
          header={
            <AccordionHeaderNumeric
              title={t('GeofenceInteractions')}
              value={interactionsData?.giTot ?? 0}
            />
          }
        >
          <AccordionTable
            hasRows={!!interactionsData?.gi?.length}
            noRowsMessage={t('NoGeofenceInteractions')}
          >
            <GridControlled<GISummaryModel>
              autoheight={true}
              sliceKey={gridSliceKeyGI}
              rowData={interactionsData?.gi}
              columnDefs={columnDefsGI}
              defaultColDef={lockColumn}
              getRowId={getRowIdGI}
            />
          </AccordionTable>
        </CasAccordion>
      </div>
    </div>
  );
}

export default AssetPanelInteractions;
